import type { ColumnDef, RowSelectionState } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import UserInitialsTableCell from '~/components/UserInitialsTableCell.vue'
import { Checkbox } from '~/components/ui/checkbox'

export const createExportDataColumns = (filterOptions: EsgTemplateFilterOptions, rowSelection: Ref<RowSelectionState>): ColumnDef<EsgTemplateWithRelations>[] => [

  {
    id: 'select',
    header: () => null,
    cell: ({ row }) => {
      const metricsCount = row.original.metrics?.length ?? 0
      const isDisabled = metricsCount === 0
      const tooltipClasses = 'absolute hidden group-hover:block left-0 top-[-20px] px-2 py-1 bg-theme-label-grey text-white text-xs rounded whitespace-nowrap ml-2 z-50'

      return h('div', {
        class: isDisabled ? 'relative group' : '',
      }, [
        h(Checkbox, {
          'disabled': isDisabled,
          'checked': rowSelection.value[row.original.id] ?? false,
          'onUpdate:checked': (value: boolean) => {
            const rowId = row.original.id
            if (value) {
              rowSelection.value = { ...rowSelection.value, [rowId]: true }
            }
            else {
              const { [rowId]: removed, ...rest } = rowSelection.value
              rowSelection.value = rest
            }
            row.toggleSelected(!!value)
          },
        }),
        isDisabled && h('div', {
          class: tooltipClasses,
        }, 'No data has been entered using this template'),
      ])
    },
    enableSorting: false,
    enableHiding: false,
  },

  // Template name column
  {
    accessorKey: 'name',
    id: 'name',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Template'),
        h(Button, {
          class: 'text-main-primary',
          variant: 'ghost',
          onClick: () => column.toggleSorting(isSorted === 'asc', true),
        }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
      ])
    },
    cell: ({ row }) => h('div', {
      class: [
        'font-medium no-localize-translate',
        row.original.metrics?.length === 0 ? 'text-gray-400 pointer-events-none' : '',
      ],
    }, row.getValue('name')),
  },

  // Metrics count column
  {
    accessorKey: 'metrics_count',
    id: 'metrics_count',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Mapped Metrics',
      filterData: filterOptions.metrics.map(metric => ({ id: metric.id, name: metric.name })),
    }),
    cell: ({ row }) => {
      const metricsCount = row.original.metrics?.length ?? 0
      return h('div', {
        class: metricsCount === 0 ? 'text-gray-400' : '',
      }, `${metricsCount} Metrics`)
    },
  },

  // Data owners column
  {
    accessorKey: 'data_owners',
    id: 'data_owner',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Data Owner',
      filterData: filterOptions.users.map(user => ({ id: user.id, name: user.name })),
    }),
    cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
  },

  // Data approvers column
  {
    accessorKey: 'data_approvers',
    id: 'data_approver',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Data Approver',
      filterData: filterOptions.users.map(user => ({ id: user.id, name: user.name })),
    }),
    cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
  },
]
